import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  LinearProgress,
  Rating,
  alpha,
  useTheme,
  CircularProgress,
  Button,
  Stack,
  Modal,
  Skeleton,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  MonetizationOn as MonetizationOnIcon,
  Assessment as AssessmentIcon,
  Star as StarIcon,
  Speed as SpeedIcon,
  Assignment as AssignmentIcon,
  Check as CheckIcon,
  CheckCircle as CheckCircleIcon,
  TrendingDown as TrendingDownIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  ContactPhone as ContactPhoneIcon,
  LocationOn as LocationOnIcon,
  Timer as TimerIcon,
  FollowTheSigns as FollowTheSignsIcon,
  AttachMoney as AttachMoneyIcon,
  Call as CallIcon,
  Task as TaskIcon,
  CallMade as CallMadeIcon,
  CallReceived as CallReceivedIcon,
  People as PeopleIcon,
  AccessTime as AccessTimeIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  ComposedChart,
} from 'recharts';
import { useAgentReport } from '../../../../contexts/AgentReportContext';
import DateRangeFilter from '../../../../components/common/DateRangeFilter';
import dayjs from 'dayjs';
import { useLoader } from '../../../../contexts/LoaderContext';
import { formatCurrency } from '../../../../utils/helper';
import { useNavigate } from 'react-router-dom';


// ------------------ COMMON DATE CONFIG ------------------
const defaultDateRange = {
  filter: { id: 'Today', label: 'Today' },
  currentStart: dayjs().startOf('day'),
  currentEnd: dayjs().endOf('day'),
  compareStart: null,
  compareEnd: null,
};

// ------------------ PERIOD TYPE HELPER ------------------
const getPeriodType = (dateRangeState) => {
  if (!dateRangeState?.filter?.id) return 'custom';
  const filterId = dateRangeState.filter.id.toLowerCase();
  if (filterId.includes('today')) return 'today';
  if (filterId.includes('week')) return 'this_week';
  if (filterId.includes('month')) return 'this_month';
  if (filterId.includes('quarter')) return 'this_quarter';
  if (filterId.includes('year')) return 'this_year';
  return 'custom';
};


// Sample Data for demonstration (fallback data)
const sampleProductivityData = {
  callsMade: 300,
  meetingsBooked: 50,
  emailsSent: 1200,
  tasksCompleted: 80,
};

const sampleTrendsData = [
  { date: '2025-09-01', leads: 50, sales: 10 },
  { date: '2025-09-08', leads: 60, sales: 12 },
  { date: '2025-09-15', leads: 70, sales: 15 },
  { date: '2025-09-22', leads: 65, sales: 13 },
  { date: '2025-09-29', leads: 75, sales: 16 },
];


const Performance = ({ agentId }) => {
  console.log('Performance component rendered with agentId:', agentId);
  const theme = useTheme();
  const navigate = useNavigate();
  const { 
    fetchSingleAgentOverview, fetchSingleAgentProductivity, fetchSingleAgentSalesFunnel,
    fetchSingleAgentLeadsSalesFunnel, fetchAgentCallPerformanceSummary, fetchSingleAgentFollowupPerformance
   } = useAgentReport();
  const { showLoader, hideLoader } = useLoader();
  const [performanceData, setPerformanceData] = useState(null);
  const [salesFunnelData, setSalesFunnelData] = useState(null);
  const [productivityData, setProductivityData] = useState(sampleProductivityData);
  const [productivityLoading, setProductivityLoading] = useState(true);
  const [callPerformanceData, setCallPerformanceData] = useState(null);
  const [callPerformanceLoading, setCallPerformanceLoading] = useState(true);
  const [followupPerformanceData, setFollowupPerformanceData] = useState(null);
  const [followupPerformanceLoading, setFollowupPerformanceLoading] = useState(true);
  const [error, setError] = useState(null);
  const chartScrollRef = useRef(null);

  const [dateRangeState, setDateRangeState] = useState(defaultDateRange);
  
  const handleDateRangeChange = (rangeObj) => {
    setDateRangeState(rangeObj);
  };

  const handleCardClick = (metricId) => {
    // Build URL params for navigation
    const params = new URLSearchParams();
    
    // Add date range filters
    if (dateRangeState) {
      if (dateRangeState.filter?.id && dateRangeState.filter.id !== 'All') {
        params.set('date_filter', dateRangeState.filter.id);
      }
      
      // Always pass start and end dates if they exist
      if (dateRangeState.currentStart && dateRangeState.currentEnd) {
        params.set('start_date', dateRangeState.currentStart.format('YYYY-MM-DD'));
        params.set('end_date', dateRangeState.currentEnd.format('YYYY-MM-DD'));
      }
    }
    
    // Handle navigation for leads cards (Contacted Leads and Connected Leads)
    if (metricId === 'totalContactedLeads') {
      params.set('stat_type', 'contacted-leads');
      params.set('agent_id', agentId);
      // Navigate to leads page with filters
      navigate(`/leads?${params.toString()}`, {
        state: { 
          from: 'reports',
          dashboardFilter: dateRangeState?.filter?.id,
          dashboardStartDate: dateRangeState?.currentStart?.format('YYYY-MM-DD'),
          dashboardEndDate: dateRangeState?.currentEnd?.format('YYYY-MM-DD')
        }
      });
      return;
    }
    
    if (metricId === 'uniqueConnectedLeads') {
      params.set('stat_type', 'connected-leads');
      params.set('agent_id', agentId);
      // Navigate to leads page with filters
      navigate(`/leads?${params.toString()}`, {
        state: { 
          from: 'reports',
          dashboardFilter: dateRangeState?.filter?.id,
          dashboardStartDate: dateRangeState?.currentStart?.format('YYYY-MM-DD'),
          dashboardEndDate: dateRangeState?.currentEnd?.format('YYYY-MM-DD')
        }
      });
      return;
    }
    
    // Handle navigation for call cards (Total Calls, Outgoing, Incoming)
    if (!['totalCalls', 'totalOutgoing', 'totalIncoming'].includes(metricId)) {
      return;
    }
    
    // Add call direction filter based on card clicked
    if (metricId === 'totalOutgoing') {
      params.set('call_direction', 'outgoing');
    } else if (metricId === 'totalIncoming') {
      params.set('call_direction', 'incoming');
    }
    // For totalCalls, no direction filter is needed (shows all calls)
    
    // Add lead_type filter to show only lead calls
    params.set('lead_type', 'lead_calls');
    
    // Add agent filter to show only this agent's calls
    params.set('agent_id', agentId);
    
    // Navigate to calls page with filters
    navigate(`/calls?${params.toString()}`);
  };

  const periodType = getPeriodType(dateRangeState);

  const [isTrendModalOpen, setIsTrendModalOpen] = useState(false);
  const [selectedTrendData, setSelectedTrendData] = useState(null);

  const handleTrendIconClick = useCallback((dataKey) => {
    // For demonstration, we'll use a simple mock-up. In a real scenario,
    // you'd fetch specific trend data based on the card and current date range.
    console.log(`Showing trend for ${dataKey}`);
    setSelectedTrendData({
      title: `${dataKey} Trend`,
      data: sampleTrendsData, // Using the existing sample data
      dataKey: dataKey.toLowerCase().replace(/ /g, ''),
      yAxisLabel: dataKey,
    });
    setIsTrendModalOpen(true);
  }, []);

  const handleCloseTrendModal = () => {
    setIsTrendModalOpen(false);
    setSelectedTrendData(null);
  };

  // Fetch data when date range changes
  useEffect(() => {
    fetchPerformanceData();
    fetchProductivityData();
    fetchSalesFunnelData();
    fetchCallPerformanceData();
    fetchFollowupPerformanceData();
  }, [dateRangeState.currentStart, dateRangeState.currentEnd]); // Run when date range changes
  const fetchPerformanceData = async () => {
    console.log('Performance - Fetching performance data');
    showLoader();
    try {
      const startDate = dateRangeState.currentStart.format('YYYY-MM-DD');
      const endDate = dateRangeState.currentEnd.format('YYYY-MM-DD');
      const [responseTime, assignedLeads, activeLeads, siteVisitCompleted, conversionRate, salesFunnelData] = await Promise.all([
        fetchSingleAgentOverview(agentId, 'response_time', startDate, endDate, periodType).catch((err) => {
          console.error('Error fetching response_time:', err);
          return null;
        }),
        fetchSingleAgentOverview(agentId, 'assigned_lead', startDate, endDate, periodType).catch((err) => {
          console.error('Error fetching assigned_lead:', err);
          return null;
        }),
        fetchSingleAgentOverview(agentId, 'active_leads', startDate, endDate, periodType).catch((err) => {
          console.error('Error fetching active_leads:', err);
          return null;
        }),
        fetchSingleAgentOverview(agentId, 'site_visit_completed', startDate, endDate, periodType).catch((err) => {
          console.error('Error fetching site_visit_completed:', err);
          return null;
        }),
        fetchSingleAgentOverview(agentId, 'conversion_rate', startDate, endDate, periodType).catch((err) => {
          console.error('Error fetching conversion_rate:', err);
          return null;
        }),
        fetchSingleAgentLeadsSalesFunnel(agentId, startDate, endDate).catch((err) => {
          console.error('Error fetching sales funnel:', err);
          return null;
        })
      ]);

      console.log('API Responses:', { responseTime, assignedLeads, activeLeads, siteVisitCompleted, conversionRate, salesFunnelData });

      // Helper function to safely get value (handle 0, null, undefined)
      const getValue = (obj, defaultValue) => {
        if (obj === null || obj === undefined) return defaultValue;
        return obj.value !== null && obj.value !== undefined ? obj.value : defaultValue;
      };

      const formattedData = [
        {
          title: 'Avg Response Time',
          value: getValue(responseTime, '0m 0s'),
          subtitle: responseTime?.subtitle ?? 'Avg Response Time',
          compare_label: responseTime?.compare_label ?? 'Last Month',
          change: responseTime?.change ?? '0%',
          trend: responseTime?.trend ?? 'up',
          icon: <TimerIcon />,
          color: 'warning',
        },
        {
          title: 'Total Leads Assigned',
          value: getValue(assignedLeads, 0),
          subtitle: assignedLeads?.subtitle ?? 'This Month',
          compare_label: assignedLeads?.compare_label ?? 'Last Month',
          change: assignedLeads?.change ?? '0%',
          trend: assignedLeads?.trend ?? 'up',
          icon: <PersonIcon />,
          color: 'primary',
        },
        {
          title: 'Active Leads (in Progress)',
          value: getValue(activeLeads, 0),
          subtitle: activeLeads?.subtitle ?? 'Currently Working',
          compare_label: activeLeads?.compare_label ?? 'Last Month',
          change: activeLeads?.change ?? '0%',
          trend: activeLeads?.trend ?? 'up',
          icon: <TaskIcon />,
          color: 'primary',
        },
        {
          title: 'Site Visits Done',
          value: getValue(siteVisitCompleted, 0),
          subtitle: siteVisitCompleted?.subtitle ?? 'Completed Visits',
          compare_label: siteVisitCompleted?.compare_label ?? 'Last Month',
          change: siteVisitCompleted?.change ?? '0%',
          trend: siteVisitCompleted?.trend ?? 'up',
          icon: <LocationOnIcon />,
          color: 'info',
        },
        {
          title: 'Bookings Count',
          value: getValue(salesFunnelData?.bookings, 0),
          subtitle: 'Total Bookings',
          compare_label: salesFunnelData?.bookings?.compare_label ?? 'Last Month',
          change: salesFunnelData?.bookings?.change ?? '0%',
          trend: salesFunnelData?.bookings?.trend ?? 'up',
          icon: <CheckCircleIcon />,
          color: 'success',
          bookingValue: salesFunnelData?.bookings_value?.value != null 
            ? formatCurrency(salesFunnelData.bookings_value.value) 
            : '₹0',
          commissionRevenue: salesFunnelData?.commission_revenue?.value != null 
            ? formatCurrency(salesFunnelData.commission_revenue.value) 
            : '₹0',
        },
        {
          title: 'Conversion Rate',
          value: getValue(conversionRate, '0%'),
          subtitle: conversionRate?.subtitle ?? 'Leads to Bookings',
          compare_label: conversionRate?.compare_label ?? 'Last Month',
          change: conversionRate?.change ?? '0%',
          trend: conversionRate?.trend ?? 'up',
          icon: <TrendingUpIcon />,
          color: 'success',
          velocity: salesFunnelData?.velocity ?? 'N/A',
        },
      ];
      
      console.log('Formatted Performance Data:', formattedData);
      setPerformanceData(formattedData);
    } catch (error) {
      console.error('Error fetching performance data:', error);
      setError(error);
      setPerformanceData(null);
    } finally {
      hideLoader(); 
    }
  };

  const fetchProductivityData = async () => {
    console.log('Performance - Fetching productivity data');
    setProductivityLoading(true);
    showLoader();
    try {
      const startDate = dateRangeState.currentStart.format('YYYY-MM-DD');
      const endDate = dateRangeState.currentEnd.format('YYYY-MM-DD');
      const productivityData = await fetchSingleAgentProductivity(agentId, startDate, endDate);
      setProductivityData(productivityData);
      
      // Auto-scroll to the end (most recent data) after data loads
      setTimeout(() => {
        if (chartScrollRef.current) {
          chartScrollRef.current.scrollLeft = chartScrollRef.current.scrollWidth;
        }
      }, 100);
    } catch (error) {
      console.error('Error fetching productivity data:', error);
      setError(error);
      setProductivityData(null);
    } finally {
      setProductivityLoading(false);
      hideLoader();
    }
  };
  
  const fetchSalesFunnelData = async () => {
    console.log('Performance - Fetching sales funnel data');
    showLoader();
    try {
      const startDate = dateRangeState.currentStart.format('YYYY-MM-DD');
      const endDate = dateRangeState.currentEnd.format('YYYY-MM-DD');
      const salesFunnelData = await fetchSingleAgentSalesFunnel(agentId, startDate, endDate);
      setSalesFunnelData(salesFunnelData);
    } catch (error) {
      console.error('Error fetching sales funnel data:', error);
      setError(error);
      setSalesFunnelData(null);
    } finally {
      hideLoader();
    }
  };

  const fetchCallPerformanceData = async () => {
    console.log('Performance - Fetching call performance data');
    setCallPerformanceLoading(true);
    showLoader();
    try {
      const startDate = dateRangeState.currentStart.format('YYYY-MM-DD');
      const endDate = dateRangeState.currentEnd.format('YYYY-MM-DD');
      const data = await fetchAgentCallPerformanceSummary('overview', startDate, endDate, agentId);
      setCallPerformanceData(data);
    } catch (error) {
      console.error('Error fetching call performance data:', error);
      setCallPerformanceData(null);
    } finally {
      setCallPerformanceLoading(false);
      hideLoader();
    }
  };

  const fetchFollowupPerformanceData = async () => {
    console.log('Performance - Fetching followup performance data');
    setFollowupPerformanceLoading(true);
    showLoader();
    try {
      const startDate = dateRangeState.currentStart.format('YYYY-MM-DD');
      const endDate = dateRangeState.currentEnd.format('YYYY-MM-DD');
      const data = await fetchSingleAgentFollowupPerformance(agentId, startDate, endDate);
      setFollowupPerformanceData(data);
    } catch (error) {
      console.error('Error fetching followup performance data:', error);
      setFollowupPerformanceData(null);
    } finally {
      setFollowupPerformanceLoading(false);
      hideLoader();
    }
  };


  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', color: 'error.main' }}>
        Error loading performance data: {error.message || 'Request failed with status code 404'}
      </Box>
    );
  }

  const renderDateFilters = () => (
    <Paper sx={{ p: 2, mb: 3, boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
      <DateRangeFilter
        onChange={handleDateRangeChange}
        dateRangeState={dateRangeState}
        showAllOption={false}
        defaultFilterId="Today"
      />
    </Paper>
  );

  return (
    <>
      {/* Date Filters */}
      {renderDateFilters()}
      
      {/* Call Performance Summary Section */}
      {(callPerformanceData || callPerformanceLoading) && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Call Performance Summary</Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mb: 3,
            }}
          >
            Volume, Mix & Response
          </Typography>
          <Grid container spacing={3}>
            {[
              {
                id: 'totalCalls',
                title: 'Total Calls',
                value: callPerformanceData?.total_calls || 0,
                subtitle: callPerformanceData ? `Fresh: ${callPerformanceData.new_calls || 0}\nFollow-ups: ${callPerformanceData.follow_up_calls || 0}` : '',
                icon: <PhoneIcon />,
                color: 'primary',
                clickable: true,
              },
              {
                id: 'totalOutgoing',
                title: 'Total Outgoing',
                value: callPerformanceData?.total_outgoing || 0,
                icon: <CallMadeIcon />,
                color: 'primary',
                clickable: true,
              },
              {
                id: 'totalIncoming',
                title: 'Total Incoming',
                value: callPerformanceData?.total_incoming || 0,
                icon: <CallReceivedIcon />,
                color: 'info',
                clickable: true,
              },
              {
                id: 'totalTalkTime',
                title: 'Total Talk Time',
                value: callPerformanceData?.total_talk_time || '0m 0s',
                icon: <AccessTimeIcon />,
                color: 'info',
              },
              {
                id: 'totalContactedLeads',
                title: 'Contacted Leads',
                value: callPerformanceData?.total_contacted_leads || 0,
                icon: <ContactPhoneIcon />,
                color: 'primary',
                clickable: true,
              },
              {
                id: 'uniqueConnectedLeads',
                title: 'Connected Leads',
                value: callPerformanceData?.unique_connected_leads || 0,
                icon: <PeopleIcon />,
                color: 'success',
                clickable: true,
              },
            ].map((metric) => (
              <Grid item xs={12} sm={6} md={6} key={metric.id}>
                <Paper
                  elevation={0}
                  onClick={() => metric.clickable && handleCardClick(metric.id)}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette[metric.color].main, 0.2)}`,
                    background: 'background.paper',
                    boxShadow: `0 2px 8px ${alpha(theme.palette[metric.color].main, 0.08)}`,
                    transition: 'all 0.3s ease',
                    height: '100%',
                    cursor: metric.clickable ? 'pointer' : 'default',
                    '&:hover': {
                      boxShadow: `0 4px 12px ${alpha(theme.palette[metric.color].main, 0.2)}`,
                      transform: metric.clickable ? 'translateY(-2px)' : 'none',
                      border: `1px solid ${alpha(theme.palette[metric.color].main, 0.4)}`,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      {callPerformanceLoading ? (
                        <>
                          <Skeleton variant="text" width="60%" height={16} sx={{ mb: 1 }} />
                          <Skeleton variant="text" width="40%" height={32} sx={{ mb: 0.5 }} />
                          <Skeleton variant="text" width="80%" height={14} />
                          {metric.chip && (
                            <Skeleton
                              variant="rectangular"
                              width={140}
                              height={24}
                              sx={{ mt: 2, borderRadius: '999px' }}
                            />
                          )}
                        </>
                      ) : (
                        <>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          mb: 1,
                        }}
                      >
                        {metric.title}
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 'bold',
                          color: theme.palette[metric.color].main,
                          mb: 0.5,
                          fontSize: '1.75rem',
                        }}
                      >
                        {metric.value}
                      </Typography>
                          {metric.subtitle && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                fontSize: '0.75rem',
                                whiteSpace: 'pre-line',
                              }}
                            >
                              {metric.subtitle}
                            </Typography>
                          )}
                          {metric.chip && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                              <Chip
                                label={metric.chip}
                                size="small"
                                sx={{
                                  bgcolor: alpha(theme.palette.success.main, 0.1),
                                  color: theme.palette.success.main,
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                }}
                              />
                              {metric.chipDetail && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    ml: 1,
                                    color: 'text.secondary',
                                    fontSize: '0.75rem',
                                  }}
                                >
                                  {metric.chipDetail}
                                </Typography>
                              )}
                    </Box>
                          )}
                        </>
                      )}
                    </Box>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette[metric.color].main, 0.1),
                        color: theme.palette[metric.color].main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 48,
                        height: 48,
                        flexShrink: 0,
                        '& svg': {
                          fontSize: '1.5rem',
                        },
                      }}
                    >
                        {callPerformanceLoading ? (
                          <Skeleton variant="circular" width={32} height={32} />
                        ) : (
                          metric.icon
                        )}
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Productivity & Discipline Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>Productivity & Discipline</Typography>
        {productivityLoading ? (
          <Box>
            <Skeleton variant="rectangular" height={40} sx={{ mb: 3, borderRadius: 2 }} />
            <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2 }} />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Total Calls and Duration Combined Chart */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3, height: '100%', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                  Total Calls & Duration
                </Typography>
                {(() => {
                  const allData = productivityData?.combinedData || [];
                  const dataLength = allData.length;
                  const daysToShow = 30;
                  const showScrollable = dataLength > daysToShow;
                  
                  // Calculate chart width: expand based on data points for better readability
                  const minWidthPerDay = 50; // Minimum pixels per day
                  const chartWidth = showScrollable 
                    ? Math.max(window.innerWidth - 100, dataLength * minWidthPerDay) 
                    : '100%';
                  
                  if (showScrollable) {
                    return (
                      <Box 
                        ref={chartScrollRef}
                        sx={{ 
                          width: '100%', 
                          overflowX: 'auto',
                          overflowY: 'hidden',
                          '&::-webkit-scrollbar': {
                            height: 8,
                          },
                          '&::-webkit-scrollbar-track': {
                            backgroundColor: alpha(theme.palette.divider, 0.1),
                            borderRadius: 4,
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.3),
                            borderRadius: 4,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.5),
                            },
                          },
                        }}
                      >
                        <Box sx={{ width: chartWidth, height: 350, minWidth: '100%' }}>
                          <ComposedChart
                            data={allData}
                            width={chartWidth}
                            height={350}
                            margin={{ top: 5, right: 30, left: 0, bottom: 80 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
                            <XAxis 
                              dataKey="date" 
                              stroke={theme.palette.text.secondary}
                              angle={-45}
                              textAnchor="end"
                              height={80}
                              interval={Math.max(0, Math.floor(dataLength / 25))}
                            />
                            <YAxis 
                              yAxisId="calls"
                              stroke={theme.palette.text.secondary}
                              label={{ value: 'Calls', angle: -90, position: 'insideLeft' }}
                            />
                            <YAxis 
                              yAxisId="duration"
                              orientation="right"
                              stroke={theme.palette.info.main}
                              label={{ value: 'Minutes', angle: 90, position: 'insideRight' }}
                            />
                            <RechartsTooltip 
                              contentStyle={{ 
                                backgroundColor: theme.palette.background.paper,
                                border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                                borderRadius: 4
                              }}
                              formatter={(value, name) => {
                                if (name === 'Duration') {
                                  return [`${value} mins`, name];
                                }
                                return [value, name];
                              }}
                            />
                            <Legend />
                            <Bar yAxisId="calls" dataKey="newCalls" name="New Calls" fill="#6495ED" stackId="calls" radius={[4, 4, 0, 0]} />
                            <Bar yAxisId="calls" dataKey="followUpCalls" name="Follow-ups" fill="#D4B87F" stackId="calls" radius={[0, 0, 4, 4]} />
                            <Line 
                              yAxisId="duration"
                              type="monotone" 
                              dataKey="duration" 
                              stroke={theme.palette.info.main} 
                              strokeWidth={3}
                              dot={{ fill: theme.palette.info.main, r: 5 }}
                              activeDot={{ r: 7 }}
                              name="Duration (mins)"
                            />
                          </ComposedChart>
                        </Box>
                      </Box>
                    );
                  } else {
                    return (
                      <ResponsiveContainer width="100%" height={350}>
                        <ComposedChart
                          data={allData}
                          margin={{ top: 5, right: 30, left: 0, bottom: 80 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
                          <XAxis 
                            dataKey="date" 
                            stroke={theme.palette.text.secondary}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            interval={0}
                          />
                          <YAxis 
                            yAxisId="calls"
                            stroke={theme.palette.text.secondary}
                            label={{ value: 'Calls', angle: -90, position: 'insideLeft' }}
                          />
                          <YAxis 
                            yAxisId="duration"
                            orientation="right"
                            stroke={theme.palette.info.main}
                            label={{ value: 'Minutes', angle: 90, position: 'insideRight' }}
                          />
                          <RechartsTooltip 
                            contentStyle={{ 
                              backgroundColor: theme.palette.background.paper,
                              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                              borderRadius: 4
                            }}
                            formatter={(value, name) => {
                              if (name === 'Duration') {
                                return [`${value} mins`, name];
                              }
                              return [value, name];
                            }}
                          />
                          <Legend />
                          <Bar yAxisId="calls" dataKey="newCalls" name="New Calls" fill="#6495ED" stackId="calls" radius={[4, 4, 0, 0]} />
                          <Bar yAxisId="calls" dataKey="followUpCalls" name="Follow-ups" fill="#D4B87F" stackId="calls" radius={[0, 0, 4, 4]} />
                          <Line 
                            yAxisId="duration"
                            type="monotone" 
                            dataKey="duration" 
                            stroke={theme.palette.info.main} 
                            strokeWidth={3}
                            dot={{ fill: theme.palette.info.main, r: 5 }}
                            activeDot={{ r: 7 }}
                            name="Duration (mins)"
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    );
                  }
                })()}
              </Paper>
            </Grid>

            {/* Followup Metrics */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                Followup Performance (Based on Tasks)
              </Typography>
              <Grid container spacing={2}>
                {(() => {
                  // Use real data from API or show loading state
                  if (followupPerformanceLoading) {
                    return (
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                          <CircularProgress />
                        </Box>
                      </Grid>
                    );
                  }

                  const followupData = [
                    { name: 'On Time', value: followupPerformanceData?.on_time || 0, color: '#4CAF50' },
                    { name: 'Delayed by 1 hr', value: followupPerformanceData?.delayed_1hr || 0, color: '#FF9800' },
                    { name: 'Delayed by 6 hrs', value: followupPerformanceData?.delayed_6hrs || 0, color: '#FF5722' },
                    { name: 'Delayed > 24 hrs', value: followupPerformanceData?.delayed_24hrs || 0, color: '#F44336' },
                  ];
                  const total = followupPerformanceData?.total || 0;

                  // Show message if no followup data
                  if (total === 0) {
                    return (
                      <Grid item xs={12}>
                        <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                          <Typography variant="body1" color="text.secondary">
                            No completed followups in the selected date range
                          </Typography>
                        </Paper>
                      </Grid>
                    );
                  }

                  return (
                    <>
                      {/* Pie Chart */}
                      <Grid item xs={12} md={5}>
                        <Paper
                          sx={{
                            p: 3,
                            borderRadius: 2,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            height: '100%',
                            minHeight: 350,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              mb: 2,
                              color: 'text.primary',
                              alignSelf: 'flex-start',
                            }}
                          >
                            Distribution
                          </Typography>
                          <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                              <Pie
                                data={followupData.filter(item => item.value > 0)}
                                cx="50%"
                                cy="45%"
                                labelLine={false}
                                label={({ name, percent, value }) => {
                                  // Only show label if percentage is > 5% to avoid clutter
                                  if (percent < 0.05) return '';
                                  const labelMap = {
                                    'On Time': 'On Time',
                                    'Delayed by 1 hr': 'Delayed 1hr',
                                    'Delayed by 6 hrs': 'Delayed 6hrs',
                                    'Delayed > 24 hrs': 'Delayed >24hrs',
                                  };
                                  const shortName = labelMap[name] || name;
                                  return `${shortName}: ${(percent * 100).toFixed(0)}%`;
                                }}
                                outerRadius={85}
                                fill="#8884d8"
                                dataKey="value"
                              >
                                {followupData.filter(item => item.value > 0).map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                              </Pie>
                              <Legend 
                                verticalAlign="bottom" 
                                height={36}
                                formatter={(value, entry) => {
                                  const item = followupData.find(d => d.name === value);
                                  const percentage = item ? ((item.value / total) * 100).toFixed(0) : 0;
                                  return `${value}: ${percentage}%`;
                                }}
                              />
                              <RechartsTooltip 
                                formatter={(value, name) => {
                                  const fullNameMap = {
                                    'On Time': 'On Time',
                                    'Delayed by 1 hr': 'Delayed by 1 hr',
                                    'Delayed by 6 hrs': 'Delayed by 6 hrs',
                                    'Delayed > 24 hrs': 'Delayed by more than 24 hours',
                                  };
                                  return [`${value} (${((value / total) * 100).toFixed(1)}%)`, fullNameMap[name] || name];
                                }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </Paper>
                      </Grid>

                      {/* Status Cards */}
                      <Grid item xs={12} md={7}>
                        <Grid container spacing={2}>
                          {followupData.map((item, index) => {
                            const percentage = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
                            const colorMap = {
                              'On Time': 'success',
                              'Delayed by 1 hr': 'warning',
                              'Delayed by 6 hrs': 'warning',
                              'Delayed > 24 hrs': 'error',
                            };
                            const iconMap = {
                              'On Time': <CheckCircleIcon />,
                              'Delayed by 1 hr': <AccessTimeIcon />,
                              'Delayed by 6 hrs': <TimerIcon />,
                              'Delayed > 24 hrs': <TrendingDownIcon />,
                            };
                            const titleMap = {
                              'On Time': 'Followup On Time',
                              'Delayed by 1 hr': 'Delayed by 1 hr',
                              'Delayed by 6 hrs': 'Delayed by 6 hrs',
                              'Delayed > 24 hrs': 'Delayed by more than 24 hours',
                            };
                            const color = colorMap[item.name] || 'primary';

                            return (
                              <Grid item xs={12} sm={6} key={index}>
                                <Paper
                                  sx={{
                                    p: 2.5,
                                    borderRadius: 2,
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                    border: `1px solid ${alpha(theme.palette[color].main, 0.2)}`,
                                    background: `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.05)} 0%, ${alpha(theme.palette[color].main, 0.02)} 100%)`,
                                    transition: 'all 0.3s ease',
                                    height: '100%',
                                    '&:hover': {
                                      boxShadow: `0 4px 12px ${alpha(theme.palette[color].main, 0.2)}`,
                                      transform: 'translateY(-2px)',
                                    },
                                  }}
                                >
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                    <Box sx={{ flex: 1 }}>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: 'text.secondary',
                                          fontSize: '0.75rem',
                                          fontWeight: 500,
                                          mb: 0.5,
                                        }}
                                      >
                                        {titleMap[item.name]}
                                      </Typography>
                                      <Typography
                                        variant="h5"
                                        sx={{
                                          fontWeight: 'bold',
                                          color: theme.palette[color].main,
                                          mb: 1,
                                        }}
                                      >
                                        {item.value}
                                      </Typography>
                                      <Box sx={{ mb: 1 }}>
                                        <LinearProgress
                                          variant="determinate"
                                          value={percentage}
                                          sx={{
                                            height: 8,
                                            borderRadius: 4,
                                            bgcolor: alpha(theme.palette[color].main, 0.1),
                                            '& .MuiLinearProgress-bar': {
                                              borderRadius: 4,
                                              bgcolor: theme.palette[color].main,
                                            },
                                          }}
                                        />
                                      </Box>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: 'text.secondary',
                                          fontSize: '0.7rem',
                                        }}
                                      >
                                        {percentage}% of {total} followups
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        bgcolor: alpha(theme.palette[color].main, 0.1),
                                        color: theme.palette[color].main,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minWidth: 48,
                                        height: 48,
                                        flexShrink: 0,
                                        '& svg': {
                                          fontSize: '1.5rem',
                                        },
                                      }}
                                    >
                                      {iconMap[item.name]}
                                    </Box>
                                  </Box>
                                </Paper>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Grid>
                    </>
                  );
                })()}
              </Grid>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Trend Modal */}
      <Modal
        open={isTrendModalOpen}
        onClose={handleCloseTrendModal}
        aria-labelledby="trend-modal-title"
        aria-describedby="trend-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 800,
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}>
          <Typography id="trend-modal-title" variant="h6" component="h2" mb={2}>
            {selectedTrendData?.title || 'Trend Data'}
          </Typography>
          <Box id="trend-modal-description" sx={{ mt: 2 }}>
            {selectedTrendData?.data ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={selectedTrendData.data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis label={{ value: selectedTrendData.yAxisLabel, angle: -90, position: 'insideLeft' }} />
                  <RechartsTooltip />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey={selectedTrendData.dataKey} 
                    stroke={theme.palette.primary.main} 
                    fill={alpha(theme.palette.primary.main, 0.2)}
                    activeDot={{ r: 8 }} 
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Typography>No trend data available.</Typography>
            )}
          </Box>
          <Button onClick={handleCloseTrendModal} sx={{ mt: 3 }} variant="contained">
            Close
          </Button>
        </Box>
      </Modal>
    </>
  );
};

export default Performance;
